import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {MapRef, Source} from "react-map-gl/mapbox";
import {useOperator} from "../../../context/OperatorContext";
import Spinner from "../../Spinner";
import Map, {VisitorDensityLayer} from "../../Map";
import {useQuery} from "react-query";
import ngage from "../../../services/ngage";
import {DateTime} from "luxon";
import {useAuth} from "../../../context/AuthContext";
import VisitorMovementLayer from "../../Map/Layers/VisitorMovementLayer";
import {flyToBounds} from "../../../services/map";
import {
  flyToVisitorPoint, getFilteredVisitorMovementData,
  getSingleVisitorMovementData,
  getVisitorOptions
} from "./VisitorsMovementMap.helpers";
import {
  DISTANCE_THRESHOLD,
  DistanceThreshold,
  ZOOM_LEVELS,
  ZoomLevel
} from "../../../pages/Insights/View/VisitorMovement";
import {LocationControl} from "./LocationControl";

// Types ----

type VisitorsMovementMapProps = {
  height: string
  since?: DateTime
  until?: DateTime
  siteId?: number | null
  showSiteSelector?: boolean
  followVisitor?: boolean
  distanceThreshold?: DistanceThreshold
  zoomLevel?: ZoomLevel
}

// Component ----

function VisitorsMovementMap({height, since, until, siteId, showSiteSelector, followVisitor = true, distanceThreshold = DISTANCE_THRESHOLD["10"], zoomLevel = ZOOM_LEVELS["2x"]}: VisitorsMovementMapProps) {
  const {settings} = useOperator();
  const {can} = useAuth();

  const mapRef = useRef<MapRef>(null);
  const selectedMovementPointRef = useRef<number>(1);
  const distanceThresholdRef = useRef<DistanceThreshold>(distanceThreshold);

  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(siteId ?? null);
  const [selectedVisitorUUID, setSelectedVisitorUUID] = useState<string | null>(null);
  const [selectedMovementPoint, setSelectedMovementPoint] = useState<number>(1);
  const [showHeatmap, setShowHeatmap] = useState<boolean>(false);

  // Fetch site data
  const {data: site, isLoading: siteLoading} = useQuery(
    ['site', {id: selectedSiteId}],
    () => ngage.sites.get(Number(selectedSiteId)),
    {enabled: can('sites:read') && !!selectedSiteId}
  )

  const sinceInTimezone = useMemo(() => site ? since?.setZone(site.timezone, { keepLocalTime: true}) : undefined, [site, since]);
  const untilInTimezone = useMemo(() => site ? until?.setZone(site.timezone, { keepLocalTime: true}) : undefined, [site, until]);
  const locale = settings?.localisation.locale ?? "en-GB";

  // Fetch visitor movement map data
  const {data: visitorMovementData, isLoading: mapDataLoading} = useQuery(
    ['maps.visitorMovement', {since: sinceInTimezone?.toISO(), until: untilInTimezone?.toISO(), siteId: selectedSiteId}],
    ({signal}) => ngage.maps.visitorMovement({since: sinceInTimezone, until: untilInTimezone, siteId: selectedSiteId}, {signal}),
    {
      enabled: !!selectedSiteId && !!sinceInTimezone && !!untilInTimezone,
      refetchOnWindowFocus: false,
    }
  );

  // Filter visitor movement map data (no distance threshold)
  const filteredVisitorMovementData = useMemo(() => {
    return getFilteredVisitorMovementData(visitorMovementData, distanceThreshold)
  }, [distanceThreshold, visitorMovementData]);

  // Get visitor movement data for map
  const singleVisitorMovementData = useMemo(() => {
      return getSingleVisitorMovementData({
        userUUID: selectedVisitorUUID,
        featureCollection: filteredVisitorMovementData,
        zone: site?.timezone,
        locale
      })
  }, [locale, selectedVisitorUUID, filteredVisitorMovementData, site?.timezone]);

  // Get visitor movement data for heatmap
  const singleVisitorMovementHeatmapData = useMemo(() => {
      return getSingleVisitorMovementData({
        userUUID: selectedVisitorUUID,
        featureCollection: visitorMovementData,
        zone: site?.timezone,
        locale
      })
  }, [locale, selectedVisitorUUID, visitorMovementData, site?.timezone]);

  const mapFeatures = useMemo(() => singleVisitorMovementData?.features ?? [], [singleVisitorMovementData]);
  const visitorOptions = useMemo(() => visitorMovementData ? getVisitorOptions(visitorMovementData, distanceThreshold) : [], [distanceThreshold, visitorMovementData]);
  const handleSiteSelect = useCallback((siteId: number | null) => setSelectedSiteId(siteId), []);

  // Update the selected site ID when the siteId prop changes
  useEffect(() => {
    if (!siteId) return;
    setSelectedSiteId(siteId);
  }, [siteId]);

  // Fly to currently selected visitor point when followVisitor prop changes
  // TODO: Try and make this work with using the state and not needing the ref. When using the state the flyTo animation doesn't work.
  useEffect(() => {
    // Reset the selected visitor movement point if distance threshold changes
    if (distanceThreshold !== distanceThresholdRef.current) {
      setSelectedMovementPoint(1);
      selectedMovementPointRef.current = 1;
    }

    if (!mapFeatures.length || !followVisitor) return;
    flyToVisitorPoint({point: selectedMovementPointRef.current, features: mapFeatures, mapRef, zoomLevel});
    distanceThresholdRef.current = distanceThreshold;
  }, [distanceThreshold, mapFeatures, selectedVisitorUUID, zoomLevel, followVisitor, distanceThresholdRef]);

  // Zoom out to site boundary if visitor isn't followed
  useEffect(() => {
    if (followVisitor || !site) return;
    flyToBounds(site.area, mapRef);
  }, [followVisitor, site]);

  // Set default selected visitor when visitorOptions changes
  useEffect(() => {
    if (!visitorOptions.length) return;
    setSelectedVisitorUUID(visitorOptions[0].value.toString())
  }, [visitorOptions]);

  // Select the visitor and set to state and ref
  function selectVisitor(id: string) {
    setSelectedVisitorUUID(id);
    setSelectedMovementPoint(1);
    selectedMovementPointRef.current = 1;
  }

  function handlePreviousPoint() {
    const previousPoint = selectedMovementPoint > 0 ? selectedMovementPoint - 1 : 0;
    setSelectedMovementPoint(previousPoint);
    selectedMovementPointRef.current = previousPoint;
    if (followVisitor) flyToVisitorPoint({ point: previousPoint, features: mapFeatures, mapRef, zoomLevel});
  }

  function handleNextPoint() {
    const nextPoint = selectedMovementPoint < mapFeatures.length ? selectedMovementPoint + 1 : selectedMovementPoint;
    setSelectedMovementPoint(nextPoint);
    selectedMovementPointRef.current = nextPoint;
    if (followVisitor) flyToVisitorPoint({point: nextPoint, features: mapFeatures, mapRef, zoomLevel});
  }

  return (
    <div className={`flex gap-6`}>
      <div className='flex-1'>
        <Map
          ref={mapRef}
          height={height}
          defaultMapStyle={settings?.audience_app.map_style ?? null}
          initialViewState={settings?.audience_app.map_state ?? null}
          hidePointsOfInterest
          showSiteSelector={showSiteSelector}
          onSelectSite={handleSiteSelect}
          siteId={selectedSiteId}
        >
          <LocationControl
            visitorOptions={visitorOptions}
            data={singleVisitorMovementData}
            isLoading={mapDataLoading}
            visitorValue={selectedVisitorUUID}
            onNextPoint={handleNextPoint}
            onPreviousPoint={handlePreviousPoint}
            selectedMovementPoint={selectedMovementPoint}
            showHeatmap={showHeatmap}
            onShowHeatmap={(checked) => setShowHeatmap(checked)}
            onSelectVisitor={(value) => {
              if (value === null || typeof value !== 'string') return;
              selectVisitor(value.toString())
            }}
          />

          {/* Data Sources */}
          {(!!singleVisitorMovementData && !showHeatmap) &&
            <Source id="visitors-movement-source" type="geojson" data={singleVisitorMovementData}>
              <VisitorMovementLayer
                id="visitors-movement"
                selectedPoint={selectedMovementPoint}
              />
            </Source>
          }

          {(!!singleVisitorMovementHeatmapData && showHeatmap)&&
            <Source id="visitors-density-source" type="geojson" data={singleVisitorMovementHeatmapData}>
              <VisitorDensityLayer id="visitors-density" />
            </Source>
          }

          {/* Loading Spinner */}
          {(mapDataLoading || siteLoading) &&
            <div className="absolute top-0 left-0 h-full w-full flex items-center justify-center pointer-events-none">
              <Spinner color={Spinner.colors.DARK} size={Spinner.sizes.MEDIUM}/>
            </div>
          }
        </Map>
      </div>
    </div>
  );
}



// Export ----

export default VisitorsMovementMap;